import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, Search, X, FileText, Upload, Eye, ExternalLink, Download } from 'lucide-react';
import api from '../services/api';
import { PageHeader, TableSkeleton, EmptyState, StatusBadge, Pagination } from '../components/PageParts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../utils/format';
import DocumentViewerModal from '../components/DocumentViewerModal';
import { openFileInNewTab, downloadFile } from '../utils/fileViewer';

const SITUATIONS = [
  { value: 'active', label: 'Ativo' },
  { value: 'suspended', label: 'Suspenso' },
  { value: 'finished', label: 'Finalizado' },
  { value: 'canceled', label: 'Cancelado' },
];

const emptyForm = {
  number: '', client_id: '', contract_type_id: '', situation: 'active',
  start_date: '', end_date: '', renewal: false, renewal_period_months: 12,
  cost_center_id: '', value: '', notes: '', file_name: '', attachment_url: ''
};

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Contracts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [situationFilter, setSituationFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [contractTypes, setContractTypes] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerFile, setViewerFile] = useState(null);
  const fileRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (search) params.search = search;
      if (situationFilter !== 'all') params.situation = situationFilter;
      if (clientFilter !== 'all') params.client_id = clientFilter;
      const { data } = await api.get('/contracts', { params });
      setItems(data.items);
      setTotal(data.total);
    } catch { toast.error('Erro ao carregar contratos'); } finally { setLoading(false); }
  };

  const loadAux = async () => {
    try {
      const [c, t, cc] = await Promise.all([
        api.get('/clients', { params: { limit: 500 } }),
        api.get('/categories', { params: { type: 'contract_type', limit: 100 } }),
        api.get('/cost-centers', { params: { limit: 100 } }),
      ]);
      setClients(c.data.items || []);
      setContractTypes(t.data.items || []);
      setCostCenters(cc.data.items || []);
    } catch {}
  };

  useEffect(() => { loadAux(); }, []);
  useEffect(() => { load(); }, [page, situationFilter, clientFilter]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 350); return () => clearTimeout(t); }, [search]); // eslint-disable-line

  const openCreate = () => { setEditing(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...emptyForm, ...c,
      start_date: c.start_date ? c.start_date.substring(0, 10) : '',
      end_date: c.end_date ? c.end_date.substring(0, 10) : '',
      value: c.value ?? '',
      contract_type_id: c.contract_type_id || '',
      cost_center_id: c.cost_center_id || '',
      file_name: c.attachment_url ? 'Anexo' : '',
      attachment_url: c.attachment_url || '',
    });
    setModalOpen(true);
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Arquivo muito grande (máx 5MB)'); return; }
    const url = await fileToDataUrl(file);
    setForm(f => ({ ...f, file_name: file.name, attachment_url: url }));
    toast.success('Arquivo carregado (clique em salvar)');
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.value === '') delete payload.value;
      else payload.value = Number(payload.value);
      if (!payload.start_date) delete payload.start_date;
      if (!payload.end_date) delete payload.end_date;
      if (!payload.contract_type_id) delete payload.contract_type_id;
      if (!payload.cost_center_id) delete payload.cost_center_id;
      // set names denormalized
      if (payload.contract_type_id) {
        payload.contract_type_name = contractTypes.find(t => t.id === payload.contract_type_id)?.name || '';
      }
      if (payload.cost_center_id) {
        payload.cost_center_name = costCenters.find(c => c.id === payload.cost_center_id)?.name || '';
      }
      if (editing) { await api.put(`/contracts/${editing.id}`, payload); toast.success('Contrato atualizado'); }
      else { await api.post('/contracts', payload); toast.success('Contrato criado'); }
      setModalOpen(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao salvar'); } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/contracts/${deleteId}`); toast.success('Contrato excluído'); setDeleteId(null); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erro ao excluir'); }
  };

  const clearFilters = () => { setSituationFilter('all'); setClientFilter('all'); setSearch(''); };
  const situationLabel = (s) => SITUATIONS.find(x => x.value === s)?.label || s;

  return (
    <div>
      <PageHeader title="Contratos" subtitle="Gestão de contratos por cliente" onAction={openCreate} actionLabel="Novo Contrato" />

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por número ou observações..." className="pl-9" />
          </div>
          <Select value={clientFilter} onValueChange={setClientFilter}>
            <SelectTrigger className="w-full md:w-56"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos clientes</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={situationFilter} onValueChange={setSituationFilter}>
            <SelectTrigger className="w-full md:w-40"><SelectValue placeholder="Situação" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {SITUATIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(search || situationFilter !== 'all' || clientFilter !== 'all') && (
            <Button variant="outline" onClick={clearFilters}><X className="h-4 w-4 mr-1" />Limpar</Button>
          )}
        </div>
      </div>

      <div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 dark:bg-neutral-900/60 text-neutral-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Número</th>
                <th className="text-left px-4 py-3 font-medium">Cliente</th>
                <th className="text-left px-4 py-3 font-medium">Tipo</th>
                <th className="text-left px-4 py-3 font-medium">Período</th>
                <th className="text-left px-4 py-3 font-medium">Valor</th>
                <th className="text-left px-4 py-3 font-medium">Situação</th>
                <th className="text-left px-4 py-3 font-medium">Anexo</th>
                <th className="text-right px-4 py-3 font-medium">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {loading ? (
                <tr><td colSpan={7} className="p-4"><TableSkeleton rows={6} cols={7} /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7}><EmptyState icon={FileText} title="Nenhum contrato cadastrado" description="Crie o primeiro contrato para um cliente." /></td></tr>
              ) : items.map((c) => (
                <tr key={c.id} className="tr-hover">
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-neutral-100 dark:bg-neutral-800 font-mono text-xs font-semibold">{c.number}</span>
                    {c.renewal && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Renovável</span>}
                  </td>
                  <td className="px-4 py-3">{c.client_name || <span className="text-neutral-400">-</span>}</td>
                  <td className="px-4 py-3">{c.contract_type_name || <span className="text-neutral-400">-</span>}</td>
                  <td className="px-4 py-3 text-xs">
                    {c.start_date ? formatDate(c.start_date) : '-'} → {c.end_date ? formatDate(c.end_date) : '-'}
                  </td>
                  <td className="px-4 py-3">{c.value ? formatCurrency(c.value) : '-'}</td>
                  <td className="px-4 py-3"><StatusBadge status={situationLabel(c.situation)} /></td>
                  <td className="px-4 py-3">
                    {c.attachment_url ? (
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={() => {
                            setViewerFile({
                              url: c.attachment_url,
                              name: c.file_name || `Contrato-${c.number}.pdf`,
                              title: `Contrato ${c.number}`
                            });
                            setViewerOpen(true);
                          }}
                          className="text-brand hover:underline font-medium text-xs truncate max-w-[140px] text-left"
                          title="Clique para visualizar"
                        >
                          {c.file_name || 'Ver anexo'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setViewerFile({
                              url: c.attachment_url,
                              name: c.file_name || `Contrato-${c.number}.pdf`,
                              title: `Contrato ${c.number}`
                            });
                            setViewerOpen(true);
                          }}
                          className="p-1 rounded hover:bg-neutral-100 text-neutral-600 hover:text-brand"
                          title="Visualizar documento"
                        >
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => openFileInNewTab(c.attachment_url, c.file_name || `Contrato-${c.number}.pdf`)}
                          className="p-1 rounded hover:bg-neutral-100 text-neutral-600 hover:text-brand"
                          title="Abrir em nova guia"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => downloadFile(c.attachment_url, c.file_name || `Contrato-${c.number}.pdf`)}
                          className="p-1 rounded hover:bg-neutral-100 text-neutral-600 hover:text-brand"
                          title="Baixar arquivo"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : <span className="text-xs text-neutral-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(c.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/30"><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} total={total} limit={limit} onPageChange={setPage} />
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar Contrato' : 'Novo Contrato'}</DialogTitle>
            <DialogDescription>Vincule o contrato a um cliente.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Número *</Label>
              <Input required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Cliente *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Tipo de contrato</Label>
              <Select value={form.contract_type_id || 'none'} onValueChange={(v) => setForm({ ...form, contract_type_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {contractTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Situação *</Label>
              <Select value={form.situation} onValueChange={(v) => setForm({ ...form, situation: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{SITUATIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Data Início</Label>
              <Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Data Término</Label>
              <Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Centro de custo</Label>
              <Select value={form.cost_center_id || 'none'} onValueChange={(v) => setForm({ ...form, cost_center_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Nenhum</SelectItem>
                  {costCenters.map(cc => <SelectItem key={cc.id} value={cc.id}>{cc.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Valor (R$)</Label>
              <Input type="number" step="0.01" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </div>
            <div className="flex items-center gap-3 pt-2 md:col-span-2">
              <Switch checked={form.renewal} onCheckedChange={(v) => setForm({ ...form, renewal: v })} />
              <div>
                <p className="text-sm font-medium">Renovação automática</p>
                <p className="text-xs text-neutral-500">Marque se o contrato será renovado automaticamente ao fim do período.</p>
              </div>
            </div>
            {form.renewal && (
              <div className="space-y-1.5 md:col-span-2">
                <Label>Período de renovação (meses)</Label>
                <Input type="number" min="1" value={form.renewal_period_months || 12} onChange={(e) => setForm({ ...form, renewal_period_months: Number(e.target.value) })} />
              </div>
            )}
            <div className="space-y-1.5 md:col-span-2">
              <Label>Anexo</Label>
              <div className="flex items-center gap-2 mt-0.5">
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} className="h-9">
                  <Upload className="h-4 w-4 mr-2" /> {form.attachment_url ? 'Trocar arquivo' : 'Escolher'}
                </Button>
                {form.file_name && <span className="text-xs text-neutral-500 truncate max-w-[200px]">{form.file_name}</span>}
              </div>
              <input ref={fileRef} type="file" onChange={handleFile} className="hidden" />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Observações</Label>
              <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <DialogFooter className="md:col-span-2">
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} className="bg-brand hover:bg-[#A81E23] text-white">{saving ? 'Salvando...' : 'Salvar'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir contrato?</AlertDialogTitle>
            <AlertDialogDescription>Soft delete: o registro será marcado como excluído mas pode ser restaurado.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DocumentViewerModal
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        fileUrl={viewerFile?.url}
        fileName={viewerFile?.name}
        title={viewerFile?.title}
      />
    </div>
  );
}
